import { useTranslation } from "react-i18next";

import { useSourceExecutions } from "@/api/sourceExecutions";
import { LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, formatNumber } from "@/utils/formatters";

export default function ExecutionsList({ itemId }: { itemId: number }) {
  const { t } = useTranslation();
  const query = useSourceExecutions({ producesItem: [itemId], limit: 100 });

  if (query.isLoading) {
    return <LoadingState label={t("items.detail.executions.loading")} />;
  }

  const rows = query.data?.results ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {t("items.detail.executions.empty")}
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("executions.columnCode")}</TableHead>
          <TableHead>{t("executions.columnSource")}</TableHead>
          <TableHead>{t("executions.columnDate")}</TableHead>
          <TableHead className="text-right">
            {t("executions.columnQuantity")}
          </TableHead>
          <TableHead>{t("executions.columnStatus")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="font-medium">{r.code}</TableCell>
            <TableCell>
              {r.source.producesItemName} @ {r.source.locationName}
            </TableCell>
            <TableCell>{formatDate(r.dueDate)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {formatNumber(Number(r.quantity))}
            </TableCell>
            <TableCell>
              {r.completed ? (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  {t("executions.statusCompleted")}
                </Badge>
              ) : (
                <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
                  {t("executions.statusOpen")}
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
